'use client';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerDescription,
  DrawerTitle,
  Button,
  COMMON_REACTIONS,
  IMessage,
  TMessageAction,
} from '@ui/components';
import { useClickOutside, useMobile } from '@ui/hooks';
import { Flag, Plus, Reply, Pencil, Copy as CopyIcon } from 'lucide-react';
import { useEffect } from 'react';

interface IProps {
  message: IMessage;
  open: boolean;
  setMessage: (message: IMessage, action: TMessageAction) => void;
  setOpen: (value: boolean) => void;
  onEmojiSelect: (emoji: string, message: IMessage) => void;
  onReply?: (message: IMessage) => void;
  onEdit?: (message: IMessage) => void;
  canReply?: boolean;
  /** False for a read-only viewer: the emoji row is replaced by a plain title. */
  canReact?: boolean;
  user_id: string;
}

export const MessageItemMobileMoreMenuActionSheet = ({
  message,
  setMessage,
  setOpen,
  open,
  onEmojiSelect,
  onReply,
  onEdit,
  canReply,
  canReact = true,
  user_id,
}: IProps) => {
  const ref = useClickOutside(() => setOpen(false));
  const isMobile = useMobile();
  const canEdit = message?.user_id === user_id;

  useEffect(() => {
    if (!isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open, setOpen]);

  return (
    <Drawer open={open && isMobile} onClose={() => setOpen(false)}>
      <DrawerContent className="z-[9999] m-auto md:max-w-sm" ref={ref}>
        <DrawerHeader className="border-b">
          <DrawerTitle className={canReact ? undefined : 'sr-only'}>
            {canReact ? (
              <div className="flex w-full flex-row justify-between gap-2">
                {COMMON_REACTIONS.map((emoji) => (
                  <Button
                    key={`emoji-${emoji}`}
                    type="icon-primary"
                    className="hover:bg-inputActive/50 h-10 w-10 rounded-sm border-none bg-transparent text-4xl"
                    onClick={() => {
                      onEmojiSelect(emoji, message);
                      setOpen(false);
                    }}>
                    {emoji}
                  </Button>
                ))}
                <Button
                  type="icon-primary"
                  className="hover:bg-inputActive/50 h-10 w-10 rounded-sm border-none bg-transparent text-4xl"
                  onClick={() => {
                    setMessage(message, 'react');
                    setOpen(false);
                  }}>
                  <Plus className="h-8 w-8" />
                </Button>
              </div>
            ) : (
              'Message options'
            )}
          </DrawerTitle>
          <DrawerDescription className={canReact ? 'font-nunito text-xs font-light' : 'sr-only'}>
            {canReact ? 'React to comment' : 'Copy or report this message'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="mt-4 h-full max-h-96 overflow-y-auto">
          {canReply && onReply && (
            <Button
              type="secondary"
              onClick={() => {
                onReply(message);
                setOpen(false);
              }}
              className="w-full rounded-none bg-white text-sm flex items-center justify-center gap-2">
              <Reply className="h-4 w-4" />
              <span className="text-sm">Reply</span>
            </Button>
          )}
          {canEdit && onEdit && (
            <Button
              type="secondary"
              onClick={() => {
                setOpen(false);
                onEdit(message);
              }}
              className="w-full rounded-none bg-white text-sm flex items-center justify-center gap-2">
              <Pencil className="h-4 w-4" />
              <span className="text-sm">Edit</span>
            </Button>
          )}
          <Button
            type="secondary"
            onClick={() => {
              if (message?.description) {
                try {
                  navigator.clipboard.writeText(message.description);
                } catch {
                  // noop
                }
              }
              setOpen(false);
            }}
            className="w-full rounded-none bg-white text-sm flex items-center justify-center gap-2">
            <CopyIcon className="h-4 w-4" />
            <span className="text-sm">Copy</span>
          </Button>
          <Button
            type="secondary"
            onClick={() => {
              setMessage(message, 'report');
              setOpen(false);
            }}
            className="w-full rounded-none bg-white text-sm flex items-center justify-center gap-2">
            <Flag className="h-4 w-4 text-red-800" />
            <span className="text-sm">Report comment</span>
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
